// Centralized analytics API utilities (production-ready)
// Uses existing backend endpoints under /origin/analytics/*
// Provides safe fallbacks (empty structures) instead of mock/random data.

const DEFAULT_SUMMARY = {
  summary: {},
  conversionFunnel: [],
  topFeatures: [],
  geographicData: [],
  dateRange: { start: null, end: null }
};

const DEFAULT_DAILY_TRENDS = [];

const DEFAULT_CONVERSION_METRICS = {
  conversionMetrics: {},
  dateRange: { start: null, end: null }
};

const DEFAULT_ENGAGEMENT_METRICS = {
  engagementMetrics: [],
  bounceRate: [],
  dateRange: { start: null, end: null }
};

const DEFAULT_REALTIME = {
  systemHealth: { totalEvents: 0, activeSessions: 0, avgEventsPerHour: 0, topCountries: [] },
  recentEvents: [],
  minuteBreakdown: [],
  pageViews1h: 0,
  avgLoadTimeMs: 0,
  topPages: [],
  liveUsers: [],
  deviceBreakdown: [],
  topCountries: [],
  shortWindow: { total: 0, errors: 0, uniqueSessions: 0, uniqueUsers: 0, windowMinutes: 5, eventsPerMinute: 0 },
  timestamp: null
};

// Utility: build ISO date range (moved from deprecated utils/analytics-api.js)
export const buildDateRange = (days = 30) => {
  const end = new Date();
  const start = new Date(end.getTime() - (parseInt(days, 10) * 24 * 60 * 60 * 1000));
  return { startDate: start.toISOString(), endDate: end.toISOString() };
};

const getToken = () => sessionStorage.getItem('afd8TvhsdjwiuuvsgjhsAfgsUhjs');

const withAuthHeaders = () => ({
  'x-access-token': getToken() || '',
  'Content-Type': 'application/json'
});

const request = async (url, fallback, options = {}) => {
  try {
    const res = await fetch(url, { headers: withAuthHeaders(), method: options.method || 'GET', body: options.body });
    if (!res.ok) {
      console.warn(`Analytics API non-OK response ${res.status} for ${url}`);
      return fallback;
    }
    const data = await res.json();
    return data || fallback;
  } catch (e) {
    console.error('Analytics API error:', e.message);
    return fallback;
  }
};

// New: trigger a background analytics job and await SSE completion with the same shape as direct GET
const triggerAnalytics = async (endpoint, query = {}, fallback, timeoutMs = 28000) => {
  // Build query string
  const params = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => {
    if (v != null) params.append(k, v);
  });
  // Prefer user token for SSE/user-targeted routing; fall back to admin token if needed
  const userToken = (typeof window !== 'undefined' && window.localStorage) ? localStorage.getItem('token') : null;
  const adminToken = getToken();
  const tokenForSse = userToken || adminToken || '';

  // Open SSE first to avoid race
  const esPath = userToken ? '/user/progress' : '/origin/progress';
  const es = new EventSource(`${esPath}${tokenForSse ? `?token=${encodeURIComponent(tokenForSse)}` : ''}`);
  let closed = false;
  const closeEs = () => { if (!closed) { try { es.close(); } catch {} closed = true; } };

  try {
  // Pass esToken for the server to route SSE per user
  const triggerUrl = `/origin/analytics/trigger?endpoint=${encodeURIComponent(endpoint)}&esToken=${encodeURIComponent(tokenForSse)}&${params.toString()}`;
  const triggerRes = await request(triggerUrl, { status: 'error' }, { method: 'POST' });
    if (!triggerRes || triggerRes.status !== 'processing' || !triggerRes.requestId) {
      // Fallback to direct GET if trigger not available
      closeEs();
      return await request(`/origin/analytics/${endpoint}?${params.toString()}`, fallback);
    }
    const { requestId } = triggerRes;

    return await new Promise((resolve) => {
      const t = setTimeout(async () => {
        // Timeout: fallback to direct GET
        closeEs();
        const direct = await request(`/origin/analytics/${endpoint}?${params.toString()}`, fallback);
        resolve(direct);
      }, timeoutMs);

      es.onmessage = async (ev) => {
        try {
          const msg = JSON.parse(ev.data || '{}');
          if (!msg || msg.type == null) return;
          if (msg.type === 'analyticsDone' && msg.requestId === requestId && msg.endpoint === endpoint) {
            clearTimeout(t);
            closeEs();
            // On completion, fetch fresh data from the original endpoint (now warm in server cache)
            const direct = await request(`/origin/analytics/${endpoint}?${params.toString()}`, fallback);
            resolve(direct);
          } else if (msg.type === 'analyticsFailed' && msg.requestId === requestId && msg.endpoint === endpoint) {
            clearTimeout(t);
            closeEs();
            // On failure, return fallback (UI remains unchanged)
            resolve(fallback);
          }
        } catch (_) { /* ignore parse errors */ }
      };
    });
  } catch (e) {
    closeEs();
    // Fallback on any unexpected error
    return await request(`/origin/analytics/${endpoint}?${params.toString()}`, fallback);
  }
};

export const fetchAnalyticsSummary = (startDate, endDate, userType = 'all') => {
  const q = {};
  if (startDate) q.startDate = startDate;
  if (endDate) q.endDate = endDate;
  if (userType && userType !== 'all') q.userType = userType;
  return triggerAnalytics('summary', q, DEFAULT_SUMMARY);
};

export const fetchDailyTrends = (startDate, endDate) => {
  const q = {};
  if (startDate) q.startDate = startDate;
  if (endDate) q.endDate = endDate;
  return triggerAnalytics('daily-trends', q, DEFAULT_DAILY_TRENDS);
};

export const fetchConversionMetrics = (startDate, endDate) => {
  const q = {};
  if (startDate) q.startDate = startDate;
  if (endDate) q.endDate = endDate;
  return triggerAnalytics('conversion', q, DEFAULT_CONVERSION_METRICS);
};

export const fetchEngagementMetrics = (startDate, endDate, userType = 'all') => {
  const q = {};
  if (startDate) q.startDate = startDate;
  if (endDate) q.endDate = endDate;
  if (userType && userType !== 'all') q.userType = userType;
  return triggerAnalytics('engagement', q, DEFAULT_ENGAGEMENT_METRICS);
};

export const fetchRealtimeAnalytics = () => {
  return request('/origin/analytics/realtime', DEFAULT_REALTIME);
};

// Admin totals
export const fetchUsersCount = () => {
  return request('/origin/get-users-count', { count: 0 });
};

// Admin totals: transactions count
export const fetchTransactionsCount = () => {
  return request('/origin/get-transactions-count', { count: 0 });
};

// New endpoints
export const fetchGeographicAnalytics = (startDate, endDate) => {
  const q = {};
  if (startDate) q.startDate = startDate;
  if (endDate) q.endDate = endDate;
  return triggerAnalytics('geographic', q, { countryData: [], languageData: [], regionData: [], cityData: [], timezoneData: [], deviceByCountry: [], conversionByCountry: [], summary: {} });
};

export const fetchPerformanceAnalytics = (startDate, endDate) => {
  const q = {};
  if (startDate) q.startDate = startDate;
  if (endDate) q.endDate = endDate;
  return triggerAnalytics('performance', q, { responseTime: [], systemMetrics: [], errorRates: [], throughput: [], serverHealth: [], apiPerformance: [], databaseMetrics: [], userExperience: [], summary: {} });
};

export const fetchDetailedConversion = (startDate, endDate) => {
  const q = {};
  if (startDate) q.startDate = startDate;
  if (endDate) q.endDate = endDate;
  return triggerAnalytics('conversion-detailed', q, {});
};

export const fetchDetailedEngagement = (startDate, endDate) => {
  const q = {};
  if (startDate) q.startDate = startDate;
  if (endDate) q.endDate = endDate;
  return triggerAnalytics('engagement-detailed', q, {});
};

export const fetchProductFunnels = (startDate, endDate) => {
  const q = {};
  if (startDate) q.startDate = startDate;
  if (endDate) q.endDate = endDate;
  return triggerAnalytics('funnels', q, { range: {}, resume: {}, coverLetter: {}, interview: {} });
};

export const fetchLifetimeAnalytics = () => {
  return triggerAnalytics('lifetime', {}, { lifetime: { messages: 0, resumesCreated: 0, resumesDownloaded: 0, resumesOptimized: 0, coverLettersCreated: 0, coverLettersDownloaded: 0, interviewsGenerated: 0, jobFetched: { lifetime: 0, session: 0 }, auth: { logins: 0, loginFailures: 0, signups: 0 } } });
};

export const fetchTopActiveUsers = (startDate, endDate) => {
  const q = {};
  if (startDate) q.startDate = startDate;
  if (endDate) q.endDate = endDate;
  return triggerAnalytics('top-active-users', q, { topUsers: [] });
};

// Helper transformers
export const transformDailyTrends = (raw = []) => {
  return raw.map(item => ({
    date: item._id || item.date || '',
    sessions: item.sessions || 0,
    users: item.users || 0,
    messages: item.messages || 0,
    loginSuccess: item.loginSuccess || 0,
    loginFailed: item.loginFailed || 0,
    signupCompleted: item.signupCompleted || 0,
    resumeCreated: item.resumeCreated || 0,
    resumeOptimized: item.resumeOptimized || 0,
    resumeDownloaded: item.resumeDownloaded || 0,
    coverCreated: item.coverCreated || 0,
    coverDownloaded: item.coverDownloaded || 0,
    interviewGenerated: item.interviewGenerated || 0,
    jobsFetched: item.jobsFetched || 0,
    careerRoadmapGenerated: item.careerRoadmapGenerated || 0,
    examGenerated: item.examGenerated || 0,
    examCustomGenerated: item.examCustomGenerated || 0,
    examPrebuiltGenerated: item.examPrebuiltGenerated || 0,
    examGraded: item.examGraded || 0,
    studyPlanGenerated: item.studyPlanGenerated || 0,
    tutorQuestions: item.tutorQuestions || 0,
    benchmarkingUsed: item.benchmarkingUsed || 0,
    insightGenerated: item.insightGenerated || 0
  }));
};

export const transformConversionFunnel = (metrics = {}) => {
  if (!metrics || Object.keys(metrics).length === 0) return [];
  const {
    totalSessions = 0,
    totalInteractions = 0,
    loginPrompts = 0,
    loginClicks = 0,
    registrations = 0,
    subscriptions = 0
  } = metrics;
  const steps = [
    { stage: 'Sessions', count: totalSessions },
    { stage: 'Interactions', count: totalInteractions },
    { stage: 'Login Prompts', count: loginPrompts },
    { stage: 'Login Clicks', count: loginClicks },
    { stage: 'Registrations', count: registrations },
    { stage: 'Subscriptions', count: subscriptions }
  ];
  const base = steps[0].count || 1;
  return steps.map(s => ({ ...s, rate: base ? parseFloat(((s.count / base) * 100).toFixed(1)) : 0 }));
};

export const transformGeographic = (geoList = []) => {
  // geoList: [{ _id: 'Nigeria', count: 123 }, ...]
  const total = geoList.reduce((sum, g) => sum + (g.count || 0), 0) || 1;
  return geoList.map(g => ({
    country: g._id || g.country || 'Unknown',
    count: g.count || 0,
    percentage: parseFloat((((g.count || 0) / total) * 100).toFixed(1))
  }));
};

export const safeNumberFormat = (num) => {
  if (num == null || Number.isNaN(num)) return 0;
  return num;
};

// Deprecated strict-fetch layer from utils/analytics-api.js intentionally not ported; if needed later,
// a stricter variant can wrap `request` and throw on non-OK responses.
